/*
 * © 2026 eggs unimedia GmbH
 * Coded with love 💛
 */

import { fetchSchedule } from '../services/graphqlService'
import { createSlug } from './slugify'

let cachedSchedules = null

/**
 * Get all available schedule slugs and dates
 * @returns {Promise<Array<{slug: string, date: Date, title: string}>>}
 */
export const getSchedules = async () => {
  if (cachedSchedules) {
    return cachedSchedules
  }

  try {
    const data = await fetchSchedule()
    const schedules = data?.data?.connectScheduleList?.items || []
    
    cachedSchedules = schedules
      .filter(s => s.title && s.date)
      .map(s => ({
        slug: s.slug || createSlug(s.title), // Use slug from GraphQL or generate from title
        date: new Date(s.date),
        title: s.title
      }))
      .sort((a, b) => a.date - b.date)
    
    return cachedSchedules
  } catch (err) {
    console.error('Failed to fetch schedules:', err)
    return []
  }
}

/**
 * Get the nearest schedule slug based on current date
 * @returns {Promise<string>} Schedule slug (e.g., 'cx-days-2026-tag-1')
 */
export const getNearestScheduleSlug = async () => {
  const schedules = await getSchedules()
  
  if (schedules.length === 0) {
    // Fallback to home if no schedules found
    return ''
  }
  
  const today = new Date()
  
  // Find schedule with smallest time difference
  let nearest = schedules[0]
  let minDiff = Math.abs(today - schedules[0].date)
  
  for (const schedule of schedules) {
    const diff = Math.abs(today - schedule.date)
    if (diff < minDiff) {
      minDiff = diff
      nearest = schedule
    }
  }
  
  return nearest.slug
}

